import { isSuccessfulStatusCode } from '../../../helpers/api-ts/request'
import { authorizedFetch } from '../../helpers/authorizedFetch'
import trans from '../../helpers/trans'
import { generateApiUrl, generateApiV3Url } from '../../helpers/url'
import { FetchCollectionResult, FetchResult, FetchResultType } from '../FetchResult'
import {
    BaseScheduleDayComment,
    BaseScheduleDayCommentCreatedResource,
    BaseScheduleDetail,
    BaseScheduleFormData,
    BaseScheduleIndex,
} from './BaseSchedule'
import {
    transformBaseScheduleDayCommentCreated,
    transformBaseScheduleDetail,
    transformBaseScheduleFormDataToCreateRequest,
    transformBaseScheduleFormDataToPatchRequest,
    transformBaseScheduleIndex,
} from './BaseScheduleTransformers'

export const getBaseSchedulesIndexApiCall = async (
    departments: string[],
): Promise<FetchCollectionResult<BaseScheduleIndex[], string>> => {
    try {
        const url = generateApiV3Url({
            endpoint: '/base-schedules',
            queryParams: {
                ...(departments.length && { 'departments[]': departments }),
            },
        })

        const response = await authorizedFetch(url)

        if (! isSuccessfulStatusCode(response.status)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = await response.json()
        const baseSchedules = data.map(transformBaseScheduleIndex)

        return {
            amountOfPages: 1,
            status: response.status,
            type: FetchResultType.Success,
            data: baseSchedules,
        }
    } catch (error) {
        console.error('[getBaseSchedulesIndexApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const getBaseScheduleApiCall = async (id: string): Promise<FetchResult<BaseScheduleDetail, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: `/base-schedules/${id}` })

        const response = await authorizedFetch(url)

        if (! isSuccessfulStatusCode(response.status)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = await response.json()
        const baseSchedule = transformBaseScheduleDetail(data)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: baseSchedule,
        }
    } catch (error) {
        console.error('[getBaseScheduleApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const postBaseScheduleApiCall = async (
    formData: BaseScheduleFormData,
): Promise<FetchResult<BaseScheduleIndex, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: '/base-schedules' })
        const body = transformBaseScheduleFormDataToCreateRequest(formData)

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
        })

        if (! isSuccessfulStatusCode(response.status)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = await response.json()

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: transformBaseScheduleIndex(data),
        }
    } catch (error) {
        console.error('[postBaseScheduleApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const patchBaseScheduleApiCall = async (
    id: string,
    formData: Partial<BaseScheduleFormData>,
): Promise<FetchResult<BaseScheduleIndex, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: `/base-schedules/${id}` })
        const body = transformBaseScheduleFormDataToPatchRequest(formData)

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify(body),
        })

        if (! isSuccessfulStatusCode(response.status)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data = await response.json()

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: transformBaseScheduleIndex(data),
        }
    } catch (error) {
        console.error('[patchBaseScheduleApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const deleteBaseScheduleApiCall = async (id: string): Promise<FetchResult<void, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: `/base-schedules/${id}` })

        const response = await authorizedFetch(url, {
            method: 'DELETE',
        })

        if (! isSuccessfulStatusCode(response.status)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: undefined,
        }
    } catch (error) {
        console.error('[deleteBaseScheduleApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const postBaseScheduleCommentApiCall = async (
    comment: string,
    baseScheduleId: string,
    dayNumber: number,
): Promise<FetchResult<BaseScheduleDayComment, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: `/base-schedules/${baseScheduleId}/days/${dayNumber}/comments` })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify({
                body: comment,
            }),
        })

        if (! isSuccessfulStatusCode(response.status)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const data: BaseScheduleDayCommentCreatedResource = await response.json()

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: transformBaseScheduleDayCommentCreated(data),
        }
    } catch (error) {
        console.error('[postBaseScheduleCommentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const deleteBaseScheduleCommentApiCall = async (commentUuid: string): Promise<FetchResult<void, string>> => {
    try {
        const url = generateApiUrl({ endpoint: `/comments/${commentUuid}` })

        const response = await authorizedFetch(url, {
            method: 'DELETE',
        })

        if (! isSuccessfulStatusCode(response.status)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: undefined,
        }
    } catch (error) {
        console.error('[deleteBaseScheduleCommentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
